import { useState, useEffect, useMemo, useCallback } from 'react';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_API_BASE_URL;

export const useUserPageLogic = (pageOwnerId, currentUserId, isAuthenticated, authUser) => {
  // ... 기존 state들 ...
  const [nickname, setNickname] = useState('');
  const [introduction, setIntroduction] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [followerList, setFollowerList] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  
  // ✨ [추가] 랭킹 히스토리 State
  const [rankingHistory, setRankingHistory] = useState([]);

  const [loading, setLoading] = useState({ profile: true, portfolio: true, follow: true, ranking: true }); // ✨ ranking 로딩 추가
  
  // ... (기존 모달 state 등은 그대로 유지) ...
  const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });
  const [followModal, setFollowModal] = useState({ show: false, type: 'followers' });

  const headers = useMemo(() => 
    isAuthenticated ? { 'Authorization': `Bearer ${authUser?.id_token}` } : {}, 
  [isAuthenticated, authUser]);

  const sanitize = (text) => DOMPurify.sanitize(text || '', { USE_PROFILES: { html: false } });

  // 데이터 로딩
  const fetchData = useCallback(async () => {
    if (!pageOwnerId) return;

    // 1. 프로필 (기존 코드)
    try {
      const res = await fetch(`${API_URL}/user/nickname/${pageOwnerId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setNickname(sanitize(data.nickname));
        setIntroduction(sanitize(data.introduction));
        setProfileImageUrl(data.profileImageUrl);
      }
    } catch (e) { console.error(e); } finally { setLoading(prev => ({ ...prev, profile: false })); }

    // 2. 포트폴리오 (기존 코드)
    try {
      const res = await fetch(`${API_URL}/portfolios/user/${pageOwnerId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPortfolios(data.map(p => ({ ...p, name: sanitize(p.name) })));
      }
    } catch (e) { console.error(e); } finally { setLoading(prev => ({ ...prev, portfolio: false })); }

    // 3. 팔로우 리스트 (기존 코드)
    try { /* ... 기존 로직 ... */
      const [followingRes, followerRes] = await Promise.all([
        fetch(`${API_URL}/user/following/${pageOwnerId}`, { headers }),
        fetch(`${API_URL}/user/followers/${pageOwnerId}`, { headers })
      ]);
      const followingData = await followingRes.json();
      const followerData = await followerRes.json();
      
      setFollowingList((followingData.following || []).map(f => ({ ...f, nickname: sanitize(f.nickname) })));
      const followers = (followerData.followers || []).map(f => ({ ...f, nickname: sanitize(f.nickname) }));
      setFollowerList(followers);
      if (currentUserId) setIsFollowing(followers.some(f => f.userId === currentUserId));
    } catch (e) { console.error(e); } finally { setLoading(prev => ({ ...prev, follow: false })); }

    // ✨ 4. [추가] 랭킹(명예의 전당) 리스트 조회
    try {
      console.log("Fetching rankings for:", pageOwnerId); // 호출 직전 확인
      const res = await fetch(`${API_URL}/portfolios/rankings/user/${pageOwnerId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        console.log("Ranking Data Received:", data); // 서버에서 온 전체 데이터 확인
        
        if (data.history) {
          console.log("Ranking History Array:", data.history); // 배열만 따로 확인
          setRankingHistory(data.history);
        } else {
          console.warn("History field is missing in response");
        }
      } else {
        console.error("Server responded with error:", res.status);
      }
    } catch (e) { 
      console.error("Network Error:", e); 
    }
    finally { setLoading(prev => ({ ...prev, ranking: false })); }

  }, [pageOwnerId, headers, currentUserId]);

  useEffect(() => { fetchData(); window.scrollTo(0, 0); }, [fetchData]);

  // ... (기존 stats 계산 로직 등 그대로 유지) ...
  const stats = useMemo(() => {
    const all = portfolios.filter(p => p.portfolioType === 'ALL');
    if (!all.length) return { allPortfolios: [], averageReturn: 0, averageSharpe: 0 };
    return {
      allPortfolios: all,
      averageReturn: all.reduce((sum, p) => sum + (p.ret_1y || 0), 0) / all.length,
      averageSharpe: all.reduce((sum, p) => sum + (p.sharpe_1y || 0), 0) / all.length,
    };
  }, [portfolios]);

  // ... (기존 handleFollowAction 등 그대로 유지) ...
  const handleFollowAction = async (method) => {
      // ... 기존 코드 ...
      if (!isAuthenticated) return setAlertInfo({ show: true, message: 'Login Required' });
      try {
        const res = await fetch(`${API_URL}/user/follow`, {
          method,
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ targetUserId: pageOwnerId }),
        });
        if (!res.ok) throw new Error();
        
        setIsFollowing(method === 'POST');
        if (method === 'POST') setFollowerList(prev => [...prev, { userId: currentUserId, nickname: authUser.profile.nickname }]);
        else setFollowerList(prev => prev.filter(u => u.userId !== currentUserId));
      } catch (e) { setAlertInfo({ show: true, message: 'Action Failed' }); }
  };

  return {
    // state에 rankingHistory 추가
    state: { nickname, introduction, profileImageUrl, portfolios, followingList, followerList, isFollowing, rankingHistory },
    stats,
    loading,
    actions: { follow: () => handleFollowAction('POST'), unfollow: () => handleFollowAction('DELETE') },
    modals: {
      alert: alertInfo,
      closeAlert: () => setAlertInfo({ ...alertInfo, show: false }),
      follow: followModal,
      openFollow: (type) => setFollowModal({ show: true, type }),
      closeFollow: () => setFollowModal({ ...followModal, show: false })
    }
  };
};